import React, { useMemo, useEffect, useState } from "react"
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table"
import { Row, Col, Card, Table, CardBody } from "reactstrap"
import Draggable from "react-draggable"
import { ResizableBox } from "react-resizable"
import { CKEditor } from "@ckeditor/ckeditor5-react"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import DOMPurify from "dompurify"
import NewPagination from "components/Common/Newpagination"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import axios from "axios"
import { fetchNodeData } from "utils/common-fetchData"
import configure from "configure"
import Swal from "sweetalert2"

const ApproveMaintenance = ({ projectDetails, total_hrs }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [datas, setData] = useState([])
  const [projectId, setProjectId] = useState(null)
  const [editModal, setEditModal] = useState(false)
  const [editData, setEditData] = useState({})
  const [fixedContent, setFixedContent] = useState(false)
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    estimation_days: "",
  })

  useEffect(() => {
    const id = projectDetails
    console.log("Project ID from projectDetails:", id)
    if (id) {
      setProjectId(id)
      setError(null)
    } else {
      console.warn("Project ID not found in projectDetails:", projectDetails)
      setError("Project details not available.")
    }
  }, [projectDetails])

  const fetchTasks = async id => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchNodeData(setLoading, `issuesMaintenance/${id}`)
      const data = res.data || []
      if (Array.isArray(data)) {
        setData(data)
      } else {
        console.error("Expected an array but got:", data)
        setData([])
        setError("Invalid data format received from server.")
      }
    } catch (err) {
      console.error("Error fetching task data:", err)
      setError("Failed to fetch tasks. Please try again later.")
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (projectId) {
      fetchTasks(projectId)
    }
  }, [projectId])

  useEffect(() => {
    const handleScroll = () => {
      setFixedContent(window.scrollY > 68)
    }
    document.addEventListener("scroll", handleScroll)
    return () => document.removeEventListener("scroll", handleScroll)
  }, [])

  const totalHours = () => {
    const sumOfTime = total_hrs[0] + total_hrs[1]
    return sumOfTime
  }

  const calculateEstimatedHours = () =>
    Array.isArray(datas)
      ? datas.reduce((sum, item) => {
          const estDays = parseFloat(item.estimation_days)
          return sum + (isNaN(estDays) ? 0 : estDays)
        }, 0)
      : 0

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ value }) => (
          <span className="text-center d-block">{value}</span>
        ),
        width: 20,
        className: "text-center",
      },
      {
        Header: "Task",
        accessor: "title",
        Cell: ({ value }) => (
          <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {value}
          </span>
        ),
        width: 230,
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordBreak: "break-word",
              maxHeight: "150px",
              overflowY: "auto",
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
          />
        ),
        width: 460,
      },
      {
        Header: "Est. Hours",
        accessor: "estimation_days",
        Cell: ({ value }) => (
          <span className="text-center d-block">{value}</span>
        ),
        width: 20,
        className: "text-center",
      },
      {
        Header: "Action",
        accessor: "action",
        className: "text-center col-md-1",
        Cell: ({ row }) => (
          <div className="d-flex justify-content-center">
            <button
              className="btn btn-sm btn-outline-secondary me-1"
              title="Edit"
              onClick={() => onEdit(row.original)}
            >
              <i className="bi bi-pencil-fill" />
            </button>
          </div>
        ),
      },
    ],
    []
  )

  const onEdit = data => {
    setEditData(data)
    setFormData({
      title: data.title || "",
      description: data.description || "",
      estimation_days: data.estimation_days || "",
    })
    setEditModal(true)
  }

  const handleEditSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    try {
      const response = await axios.put(
        `${configure.nodeApi}/editMTasks/${editData.id}`,
        {
          title: formData.title,
          description: formData.description,
          estimation_days: formData.estimation_days,
        },
        {
          headers: {
            Authorization: `Bearer ${secureLocalStorage.getItem("auth_token")}`,
          },
        }
      )
      if (response.status !== 200) {
        Swal.fire({
          icon: "failed",
          text: "Failed to update task. Please try again.",
        })
      } else {
        Swal.fire({
          icon: "success",
          text: "Task updated successfully.",
        })
        const data = response.data
        setData(response.data.data)
        setEditModal(false)
      }
    } catch (err) {
      console.error("Error updating task:", err)
      setError("Failed to update task. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    prepareRow,
  } = useTable(
    {
      columns,
      data: datas,
      initialState: {
        sortBy: [{ id: "id", desc: false }],
        pageSize: 50,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize } = state

  if (!projectId) {
    return (
      <Card>
        <CardBody>
          <div className="alert alert-warning" role="alert">
            Project details not available. Please select a valid project.
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <div className={`note-sticky-form ${editModal ? "d-block" : "d-none"}`}>
        <Draggable
          onStart={e => {
            if (e.target.type === "radio" || e.target.tagName === "LABEL") {
              e.stopPropagation()
              return false
            }
            return true
          }}
        >
          <ResizableBox
            width={900}
            style={{
              position: "fixed",
              top: "10%",
              right: "2%",
              zIndex: "1055",
              backgroundColor: "#fff",
            }}
          >
            <div className="modal-header pe-4">
              <h5 className="modal-title mt-0 mb-3">Edit Issue/Bug</h5>
              <button
                type="button"
                onClick={() => setEditModal(false)}
                className="btn-modal-close"
              >
                <span aria-hidden="true" className="btn-modal-span">
                  ×
                </span>
              </button>
            </div>

            <div className="p-0">
              <form onSubmit={handleEditSubmit}>
                <div
                  className="row gx-3 gy-3 mt-2"
                  style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <div className="col-md-4">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Title"
                      value={formData.title}
                      onChange={e => handleFormChange("title", e.target.value)}
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Estimated Hours</label>
                    <input
                      type="number"
                      placeholder="Enter Estimated Hours"
                      className="form-control"
                      value={formData.estimation_days}
                      onChange={e =>
                        handleFormChange("estimation_days", e.target.value)
                      }
                      required
                    />
                  </div>

                  <div className="col-md-12">
                    <label className="form-label">Description</label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={formData.description || "<p></p>"}
                      onChange={(event, editor) =>
                        handleFormChange("description", editor.getData())
                      }
                    />
                  </div>
                </div>

                <div className="my-2 text-end">
                  <button type="submit" className="btn btn-save btn-label">
                    <i className="fas fa-save me-2 label-icon"></i>
                    Update
                  </button>
                </div>
              </form>
            </div>
          </ResizableBox>
        </Draggable>
      </div>

      {loading && <Loader />}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <Card>
        <CardBody>
          <div className="moveUp">
            <Row>
              <Col className="col-1">
                <select
                  className="border-radius me-4 form-select"
                  style={{ width: "100px" }}
                  value={pageSize}
                  onChange={e => setPageSize(Number(e.target.value))}
                >
                  {[10, 25, 50, 100].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </Col>
              <Col className="col-2">
                <div className="d-flex gap-2 align-items-center general-box bg-light-red justify-content-center">
                  <h5 className="text-purple mb-0">Total Hours:</h5>
                  <h5 className="text-primary mb-0">{totalHours()}</h5>
                </div>
              </Col>
              <Col className="col-2">
                <div className="d-flex gap-2 align-items-center general-box bg-light-red justify-content-center">
                  <h5 className="text-purple mb-0">Estd. Hours:</h5>
                  <h5 className="text-primary mb-0">
                    {calculateEstimatedHours()}
                  </h5>
                </div>
              </Col>
            </Row>
            <div className="position-relative mt-3">
              <Table className="table table-bordered" {...getTableProps()}>
                <thead
                  className={`${
                    fixedContent && page.length >= 10
                      ? "fixed_complete_process_headers"
                      : "fixed-table-header"
                  }`}
                >
                  {headerGroups.map((headerGroup, i) => (
                    <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column, index) => (
                        <th
                          key={index}
                          style={{
                            width: column.width,
                            maxWidth: column.width,
                          }}
                          className={` ${
                            column.className || ""
                          } customShortIcon custom-text`}
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                        >
                          {column.render("Header")}
                          <span className="ms-2 cursor-pointer customApprovalIcon">
                            <i
                              title="Ascending"
                              className={`bi bi-arrow-up info-icon ${
                                column.isSorted && !column.isSortedDesc
                                  ? "text-dark"
                                  : ""
                              }`}
                            ></i>
                            <i
                              title="Descending"
                              className={`bi bi-arrow-down info-icon ${
                                column.isSorted && column.isSortedDesc
                                  ? "text-dark"
                                  : ""
                              }`}
                            ></i>
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row)
                    return (
                      <tr className="row-hover" {...row.getRowProps()} key={i}>
                        {row.cells.map((cell, index) => (
                          <td
                            key={index}
                            style={{
                              width: cell.column.width,
                              maxWidth: cell.column.width,
                            }}
                            className={`t-col-${index + 1} ${
                              cell.column.className || ""
                            }`}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    )
                  })}
                  {page.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} className="text-center">
                        No Record Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <Row className="justify-content-md-end justify-content-center align-items-center">
              <NewPagination
                gotoPage={gotoPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                pageSize={pageSize}
                page={page}
                data={datas}
              />
            </Row>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default ApproveMaintenance